import React, { ChangeEvent } from 'react'
import { MutationFunction } from '@apollo/client/react'
import { IIssueText } from '@scentregroup/shared/components/conditional-issue-text'
import { ProductModifierType } from '../../types/product'
import {
  MarketplaceProductOptionType,
  MarketplaceProduct,
} from '../../types/marketplace-product'
import { IMKPProductValues, ProductFields } from '../../types/product-form'
import { IFormChangeEvent, IUseFormReturnType } from '../../form/use-form'
import { Input, InputType } from '../../types/product-form-inputs'
import { IAddProductToCartInput } from '../../types/add-to-cart'
import { IAddToCartResponse } from '../../types/cart'
import { createMarketPlaceMutationInput } from '../../lib/add-to-cart'
import { useEffectAfterChange } from '../../lib/use-effect-after-change'
import {
  toInputOptionValues,
  fromOptionsToInputOptionValues,
} from '../../lib/product-form-inputs'
import { useCookie, CookieName } from '../../lib/cookie'
import { IVisualGuideInfo } from '../../lib/use-visual-guide-context'
import trackMkpEvent, { labels } from '../../lib/market-place-track-event'
import ProductFormPresentational, { IAnalyticsData } from './presentational'

interface IProductForm {
  product: MarketplaceProduct
  form: IUseFormReturnType<IMKPProductValues, OnSubmitFnArgs>
  mutation: MutationFunction<IAddToCartResponse, IAddProductToCartInput>
  cartItemId?: string
  analyticsData: IAnalyticsData
  maxQuantity: number
  stockError: IIssueText | null
  disabled: boolean
  handleBlur?: (e: IFormChangeEvent) => void
}

export type OnSubmitFnArgs = [
  MutationFunction<IAddToCartResponse, IAddProductToCartInput>,
  (values: IMKPProductValues) => IAddProductToCartInput,
]
const trackAddToCartFormError = (): void =>
  trackMkpEvent.viewed(labels.ADD_TO_CART_FORM_ERROR)

const ProductForm: React.FunctionComponent<
  React.PropsWithChildren<IProductForm>
> = ({
  product,
  form,
  mutation,
  cartItemId,
  analyticsData,
  maxQuantity,
  stockError,
  disabled,
  handleBlur,
}) => {
  const [cartId] = useCookie(CookieName.CART)

  const shouldResetOnVariantSelect =
    maxQuantity > 0 && (form.values.quantity || 1) > maxQuantity
  if (shouldResetOnVariantSelect) {
    form.handleBlur({
      name: ProductFields.QUANTITY,
      value: Math.max(maxQuantity, 1),
    })
  }

  const trackAddToCartFromErrorEvent = useEffectAfterChange(
    trackAddToCartFormError,
    !!Object.values(form.errors).filter(Boolean).length
  )

  const estimatedOptionRows = product.options
    .filter(option => option.type === MarketplaceProductOptionType.DROPDOWN)
    .reduce((rows, option) => {
      return rows + Math.ceil(option.values.length / 3)
    }, 0)
  const allowButtonSelect = estimatedOptionRows <= 3
  const optionInputs: Input[] = product.options
    .map((option, index) => {
      const isPrimaryOption = index === 0

      const insertVisualGuideProperty = (): {
        visualGuideInfo: IVisualGuideInfo
      } | null => {
        if (option.visualGuide) {
          return {
            visualGuideInfo: {
              ...option.visualGuide,
              optionId: option.id,
            },
          }
        } else {
          return null
        }
      }
      switch (option.type) {
        case MarketplaceProductOptionType.DROPDOWN:
          return {
            id: option.id,
            type: InputType.SELECT,
            label: option.displayName,
            count: option.values.length,
            options: fromOptionsToInputOptionValues(
              option.values,
              product,
              form.values,
              option.id,
              isPrimaryOption,
              allowButtonSelect
            ),
            ...insertVisualGuideProperty(),
          }
      }
    })
    .sort(({ count: a }, { count: b }) => {
      if ((a === 1 && b === 1) || (a !== 1 && b !== 1)) {
        return 0
      }
      return a - b
    })

  const modifierInputs: Input[] = product.modifiers.map(modifier => {
    switch (modifier.type) {
      case ProductModifierType.TEXT:
        return {
          id: modifier.id,
          type: InputType.TEXTFIELD,
          label: modifier.displayName,
        }
      case ProductModifierType.CHECKBOX:
        return {
          id: modifier.id,
          type: InputType.CHECKBOX,
          label: modifier.displayName,
          options: toInputOptionValues(modifier.values),
        }
      case ProductModifierType.DROPDOWN:
        return {
          id: modifier.id,
          type: InputType.SELECT,
          label: modifier.displayName,
          options: toInputOptionValues(modifier.values),
        }
    }
  })

  const _handleSubmit = (e: ChangeEvent<HTMLButtonElement>): void => {
    form.handleSubmit(
      e,
      mutation,
      createMarketPlaceMutationInput(product, cartId, cartItemId)
    )

    trackMkpEvent.clicked(labels.ADD_TO_CART, {
      items: [{ product_id: analyticsData.uniqueProductId }],
    })
    trackAddToCartFromErrorEvent()
  }

  const primaryOptionKey = `option-${product.options[0]?.id}`

  function _handleBlur(e: IFormChangeEvent): void {
    const isPrimaryOption = primaryOptionKey === e.name
    const currentValue = form.values[e.name]

    if (isPrimaryOption && currentValue !== e.value && !allowButtonSelect) {
      const optionKeys = product.options
        .map(option => `option-${option.id}`)
        .filter(key => key !== primaryOptionKey)
      form.clearValues(optionKeys)
    }

    form.handleBlur(e)
    handleBlur && handleBlur(e)
  }

  return (
    <ProductFormPresentational
      {...form}
      handleBlur={_handleBlur}
      analyticsData={analyticsData}
      options={optionInputs}
      modifiers={modifierInputs}
      maxQuantity={maxQuantity}
      handleSubmit={_handleSubmit}
      disabled={disabled}
      stockError={stockError}
      allowButtonSelect={allowButtonSelect}
    />
  )
}

export default ProductForm
